import { useEffect, useRef } from 'react';

function isTypingTarget(el) {
    if (!el || typeof el !== 'object') return false;
    const tag = el.tagName;
    return tag === 'INPUT'
        || tag === 'TEXTAREA'
        || tag === 'SELECT'
        || el.isContentEditable === true;
}

/** A PDF, whether the clipboard names its type or only its extension. */
export const isPastedPdf = (file) => file?.type === 'application/pdf'
    || /\.pdf$/i.test(file?.name || '');

export const isPastedImage = (file) => (file?.type || '').startsWith('image/');

/**
 * Ctrl/Cmd+V anywhere on a tool page drops the copied file straight into it.
 *
 * The listener sits on the window rather than the drop zone: a paste is not
 * aimed at a particular box, and the zone is rarely what has focus. Pastes into
 * a text field (a watermark caption, a page range) are left alone.
 *
 * `onFiles` is held in a ref, so a call site may pass an inline arrow without
 * re-subscribing the window on every render.
 *
 * @param {(files: File[]) => void} onFiles receives the accepted files.
 * @param {{ enabled?: boolean, accept?: (file: File) => boolean,
 *   rename?: (file: File, index: number) => File }} [options]
 *   `enabled: false` ignores pastes (while a run is in flight, say); `rename`
 *   is for clipboard files whose own name is no use.
 */
export default function useFilePaste(onFiles, { enabled = true, accept, rename } = {}) {
    const handler = useRef(onFiles);
    handler.current = onFiles;

    useEffect(() => {
        if (!enabled || typeof accept !== 'function') return undefined;

        const handlePaste = (event) => {
            if (isTypingTarget(event.target) || isTypingTarget(document.activeElement)) return;

            const files = Array.from(event.clipboardData?.items || [])
                .filter((item) => item.kind === 'file')
                .map((item) => item.getAsFile())
                .filter((file) => file && accept(file))
                .map((file, i) => (rename ? rename(file, i) : file));

            if (!files.length || typeof handler.current !== 'function') return;
            event.preventDefault();
            handler.current(files);
        };

        window.addEventListener('paste', handlePaste);
        return () => window.removeEventListener('paste', handlePaste);
    }, [enabled, accept, rename]);
}

/**
 * The PDF tools' version: a file copied in the file manager keeps its own name,
 * which is what these tools show in the list and write back out.
 */
export function usePdfPaste(onFiles, options) {
    return useFilePaste(onFiles, { ...options, accept: isPastedPdf });
}
